import React, { useRef, useState } from 'react';
import { groupBy } from 'lodash';
import {
  Button,
  Checkbox,
  Combobox,
  Dialog,
  Flex,
  GridAlpha,
  Notice,
  SrOnly,
  Table,
  Text,
  TextInput,
  useToasts,
} from '@applyboard/crystal-ui';
import { PlusOutlineIcon } from '@applyboard/ui-icons';
import { useTranslation } from '@applyboard/ui-utils';
import { useReloadApplicationPageAggregatedData } from '../../data/aggregatedData';
import {
  type QueryParams,
  type RequirementTemplate,
  useRequirementTemplates,
} from '../../data/requirementTemplates';
import { useApplication } from '../../data/application';
import { type RequirementTemplateType } from '~/types';
import { type ApplicationStage, useApplicationStages } from '../../data/applicationStages';
import { useRequirementCategories } from '../../data/requirementCategories';
import { RequirementDescription } from '../RequirementDescription';
import {
  type SelectedRequirementTemplate,
  useAddApplicationRequirements,
} from '../../data/applicationRequirements';
import { ApplicationRejectedIllustration } from '~/illustrations';
import { useRequirementListEventTracker } from '../../utils/eventTracking';

export function AddRequirementsButton({
  isOpen,
  onOpenChange,
}: {
  isOpen?: boolean;
  onOpenChange?: (newOpen: boolean) => void;
}) {
  const { isDialogOpen, setIsDialogOpen } = useDialogOpenState({
    controlledIsOpen: isOpen,
  });

  const [selectedRequirements, setSelectedRequirements] = useState<TableData[]>([]);
  const [showNoRequirementSelectedMessage, setShowNoRequirementSelectedMessage] = useState(false);
  const { application } = useApplication();
  const { addApplicationRequirements, isAddingApplicationRequirements } =
    useAddApplicationRequirements(application.id);
  const { t } = useTranslation('AddRequirementsButton');
  const toasts = useToasts();
  const modalRef = useRef<HTMLDivElement>(null);
  const { reloadApplicationPageAggregatedData } = useReloadApplicationPageAggregatedData();

  const { trackEvent } = useRequirementListEventTracker();

  const scrollToNotice = () => {
    if (modalRef.current) {
      modalRef.current.scrollTo(0, 0);
    }
  };

  const addSelectedRequirements = () => {
    if (selectedRequirements.length === 0) {
      setShowNoRequirementSelectedMessage(true);
      scrollToNotice();
      return;
    }
    const selectedRequirementTemplates: SelectedRequirementTemplate[] = selectedRequirements.map(
      ({ number, requirementTemplateType }) => ({
        number,
        requirementTemplateType,
      }),
    );
    addApplicationRequirements(selectedRequirementTemplates, {
      onSuccess: async () => {
        trackEvent({
          event: 'Add requirements - Added additional requirements',
          data: {
            newRequirements: selectedRequirements.map(requirement => ({
              requirementCategory: requirement.category,
              applicationStage: requirement.applicationStage,
              requirementTemplateReferenceNumber: requirement.number,
              requirementTitle: requirement.internalTitle,
            })),
          },
        });
        toasts.positive(t('addRequirementsSuccess'));
        onOpenChange ? onOpenChange(false) : setIsDialogOpen(false);
      },
      onError: error => {
        if (error instanceof Error) {
          toasts.negative(error);
        }
        // Reload the aggregated data in case of partial success
        reloadApplicationPageAggregatedData();
      },
    });
  };

  function onDialogOpenChange(isOpen: boolean) {
    onOpenChange ? onOpenChange(isOpen) : setIsDialogOpen(isOpen);
    if (isOpen) {
      setSelectedRequirements([]);
      setShowNoRequirementSelectedMessage(false);
    } else {
      trackEvent({
        event: 'Add requirements - Dismissed additional requirements',
      });
    }
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={onDialogOpenChange} size="xl">
      {!onOpenChange && (
        <Dialog.Trigger>
          <Button
            size="sm"
            intent="primary"
            emphasis="outlined"
            leadIcon={PlusOutlineIcon}
            onClick={() =>
              trackEvent({
                event: 'Clicked add requirements',
              })
            }
          >
            {t('addRequirementsButton')}
          </Button>
        </Dialog.Trigger>
      )}
      <Dialog.Heading>{t('addRequirementsModalTitle')}</Dialog.Heading>
      <Dialog.Content>
        <RequirementTemplatesTableWrapper
          selectedRequirements={selectedRequirements}
          onSelectRequirements={setSelectedRequirements}
          showNoRequirementSelectedMessage={showNoRequirementSelectedMessage}
          setShowNoRequirementSelectedMessage={setShowNoRequirementSelectedMessage}
          modalRef={modalRef}
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Flex justify="end" gap={2} grow={1}>
          <Dialog.CloseButton>{t('addRequirementsModalClose')}</Dialog.CloseButton>
          <Button
            intent="primary"
            onClick={addSelectedRequirements}
            loading={isAddingApplicationRequirements}
          >
            {t('addRequirementsModalAdd', { count: selectedRequirements.length })}
          </Button>
        </Flex>
      </Dialog.Actions>
    </Dialog>
  );
}

function useDialogOpenState({ controlledIsOpen }: { controlledIsOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const updateIsDialogOpen = (newOpen: boolean) => {
    if (controlledIsOpen === undefined) {
      setIsOpen(newOpen);
    }
  };

  return {
    isDialogOpen: controlledIsOpen === undefined ? isOpen : controlledIsOpen,
    setIsDialogOpen: updateIsDialogOpen,
  };
}

const RequirementTemplatesTableWrapper = ({
  selectedRequirements,
  onSelectRequirements,
  showNoRequirementSelectedMessage,
  setShowNoRequirementSelectedMessage,
  modalRef,
}: {
  selectedRequirements: TableData[];
  onSelectRequirements: (requirement: TableData[]) => void;
  showNoRequirementSelectedMessage: boolean;
  setShowNoRequirementSelectedMessage: (showNoRequirementSelectedMessage: boolean) => void;
  modalRef: React.RefObject<HTMLDivElement>;
}) => {
  const [queryParams, setQueryParams] = useState<QueryParams>({ sort: 'number.asc' });
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const { applicationStages } = useApplicationStages();
  const { requirementCategories, isLoadingRequirementCategories } = useRequirementCategories();
  const { t } = useTranslation('AddRequirementsButton');

  const requirementsGroupedByStage = groupBy(requirementCategories, 'applicationStage');
  const visibleRequirementStages = applicationStages?.filter(
    stage => requirementsGroupedByStage[stage.id],
  );

  return (
    <Flex direction="column" gap={2}>
      <div>
        <GridAlpha
          columns="repeat(12, 1fr)"
          columnGap={2}
          rowGap={2}
          rows={{ xs: 'repeat(3, 1fr)', sm: 'auto' }}
        >
          <GridAlpha.Item
            columnStart={1}
            columnEnd={{
              xs: 13,
              sm: 5,
            }}
            rowStart={1}
          >
            <TextInput
              label={t('queryParams.requirementIdLabel')}
              value={queryParams['filter[q]'] || ''}
              placeholder={t('queryParams.requirementIdPlaceholder')}
              onChange={value => {
                setQueryParams({ ...queryParams, 'filter[q]': value, 'page[number]': 1 });
                setExpandedRows([]);
              }}
              size="sm"
            />
          </GridAlpha.Item>
          <GridAlpha.Item
            columnStart={{
              xs: 1,
              sm: 5,
            }}
            columnEnd={{
              xs: 13,
              sm: 9,
            }}
            rowStart={{
              xs: 2,
              sm: 1,
            }}
          >
            <Combobox
              label={t('queryParams.categoryLabel')}
              onChange={value => {
                setQueryParams({ ...queryParams, 'filter[category]': value, 'page[number]': 1 });
                setExpandedRows([]);
              }}
              value={queryParams['filter[category]'] || ''}
              placeholder={t('queryParams.categoryPlaceholder')}
              loading={isLoadingRequirementCategories}
            >
              {visibleRequirementStages?.map(stage => (
                <Combobox.OptionGroup
                  key={stage.id}
                  label={stage.displayName}
                  groupValue={stage.id}
                >
                  {requirementsGroupedByStage[stage.id].map(category => (
                    <Combobox.Option
                      key={category.key}
                      value={category.key}
                      label={category.label}
                    />
                  ))}
                </Combobox.OptionGroup>
              ))}
            </Combobox>
          </GridAlpha.Item>
          <GridAlpha.Item
            columnStart={{
              xs: 1,
              sm: 9,
            }}
            columnEnd={13}
            rowStart={{
              xs: 3,
              sm: 1,
            }}
          >
            <Combobox
              label={t('queryParams.stageLabel')}
              onChange={value => {
                setQueryParams({
                  ...queryParams,
                  'filter[application_stage]': value,
                  'page[number]': 1,
                });
                setExpandedRows([]);
              }}
              value={queryParams['filter[application_stage]'] || ''}
              placeholder={t('queryParams.stagePlaceholder')}
            >
              {applicationStages?.map(stage => (
                <Combobox.Option key={stage.id} value={stage.id} label={stage.displayName} />
              ))}
            </Combobox>
          </GridAlpha.Item>
        </GridAlpha>
      </div>
      {/* TODO: See if we can expose breakpoints from crystal-ui to replace hardcode media queries */}
      <div
        css={{
          maxHeight: 'calc(100vh - 358px)',
          '@media(max-width: 600px)': {
            maxHeight: 'calc(100vh - 480px)',
          },
          overflow: 'auto',
        }}
        ref={modalRef}
      >
        <RequirementTemplatesTable
          queryParams={queryParams}
          onQueryParamsChange={setQueryParams}
          onShowNoRequirementSelectedMessage={setShowNoRequirementSelectedMessage}
          selectedRequirements={selectedRequirements}
          onSelectRequirements={onSelectRequirements}
          showNoRequirementSelectedMessage={showNoRequirementSelectedMessage}
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
        />
      </div>
    </Flex>
  );
};

function RequirementTemplatesTable({
  selectedRequirements,
  onSelectRequirements,
  onQueryParamsChange,
  onShowNoRequirementSelectedMessage,
  queryParams,
  showNoRequirementSelectedMessage,
  expandedRows,
  setExpandedRows,
}: {
  selectedRequirements: TableData[];
  onSelectRequirements: (requirement: TableData[]) => void;
  onQueryParamsChange: (params: QueryParams) => void;
  onShowNoRequirementSelectedMessage: (showMessage: boolean) => void;
  queryParams: QueryParams;
  showNoRequirementSelectedMessage: boolean;
  expandedRows: number[];
  setExpandedRows: (expandedRows: number[]) => void;
}) {
  const { t } = useTranslation('AddRequirementsButton');
  const { requirementTemplates, pageNumber, pageSize, totalCount, isLoadingRequirementTemplates } =
    useRequirementTemplates(queryParams);
  const { applicationStages } = useApplicationStages();
  const data = getTableData(requirementTemplates, applicationStages);

  if (data.length === 0 && !isLoadingRequirementTemplates) {
    return <NoRequirementsFound />;
  }

  return (
    <>
      {showNoRequirementSelectedMessage && (
        <Flex pt={2}>
          <Notice intent="negative">{t('noRequirementSelectedMessage')}</Notice>
        </Flex>
      )}
      <Table
        overflow="scroll"
        loading={isLoadingRequirementTemplates}
        columns={[
          {
            accessorKey: 'number',
            header: () => <SrOnly>{t('selectRequirementTemplate')}</SrOnly>,
            cell: (props: (typeof data)[number]) => (
              <div
                css={{
                  paddingLeft: '16px',
                }}
              >
                <Checkbox
                  aria-label={`${props.number}: ${props.internalTitle}`}
                  onChange={value => {
                    const requirement = {
                      ...props,
                    };
                    if (value) {
                      onSelectRequirements(selectedRequirements.concat(requirement));
                      onShowNoRequirementSelectedMessage(false);
                    } else {
                      onSelectRequirements(
                        selectedRequirements.filter(
                          selectedItem => selectedItem.number !== requirement.number,
                        ),
                      );
                    }
                  }}
                  value={selectedRequirements.some(
                    selectedItem => selectedItem.number === props.number,
                  )}
                />
              </div>
            ),
          },
          {
            accessorKey: 'number',
            header: t('requirement'),
            cell: (props: (typeof data)[number]) => (
              <div
                css={{
                  padding: '0 16px',
                }}
              >
                <Text
                  lineClamp={1}
                  wordBreak="word"
                >{`${props.number}: ${props.internalTitle}`}</Text>
              </div>
            ),
            enableSorting: true,
          },
          {
            accessorKey: 'category',
            header: t('category'),
            cell: (props: (typeof data)[number]) => (
              <div
                css={{
                  maxWidth: '212px',
                  padding: '0 16px',
                }}
              >
                <Text lineClamp={1} wordBreak="word">
                  {props.categoryDisplayName}
                </Text>
              </div>
            ),
            enableSorting: true,
          },
          {
            accessorKey: 'application_stage',
            header: t('stage'),
            cell: (props: (typeof data)[number]) => (
              <div
                css={{
                  maxWidth: '212px',
                  padding: '0 16px',
                }}
              >
                <Text lineClamp={1} wordBreak="word">
                  {props.applicationStage}
                </Text>
              </div>
            ),
            enableSorting: true,
          },
          {
            accessorKey: 'expand',
            cell: Table.ExpandCell,
            header: '',
          },
        ]}
        data={data}
        renderSubComponent={renderSubComponent}
        onSort={sort => {
          if (sort.length > 0) {
            onQueryParamsChange({
              ...queryParams,
              sort: `${sort[0].id}.${sort[0].desc ? 'desc' : 'asc'}`,
            });
          } else {
            onQueryParamsChange({
              ...queryParams,
              sort: 'number.asc',
            });
          }
          setExpandedRows([]);
        }}
        sort={getSort(queryParams.sort)}
        sortOptions={['asc', 'desc']}
        onExpand={setExpandedRows}
        expanded={expandedRows}
      >
        <Table.Pagination
          currentPage={pageNumber || 1}
          numItems={totalCount}
          onPageChange={number => {
            onQueryParamsChange({ ...queryParams, 'page[number]': number });
            setExpandedRows([]);
          }}
          pageSize={pageSize || 10}
        />
      </Table>
    </>
  );
}

type TableData = {
  applicationStage: string;
  category: string;
  categoryDisplayName: string;
  description: string;
  id: string;
  internalTitle: string;
  number: number;
  requirement: string;
  requirementTemplateType: RequirementTemplateType;
};

function getTableData(
  requirementTemplates: RequirementTemplate[] | undefined,
  applicationStages: ApplicationStage[] | undefined,
): TableData[] {
  if (!requirementTemplates || !applicationStages) {
    return [];
  }

  return requirementTemplates.map(requirementTemplate => {
    const stages = new Map(
      applicationStages.map(stage => [stage.id.replace(/_/g, '-'), stage.displayName]),
    );

    return {
      applicationStage:
        stages.get(requirementTemplate.applicationStage) || requirementTemplate.applicationStage,
      category: requirementTemplate.category,
      categoryDisplayName: requirementTemplate.categoryDisplayName,
      description: requirementTemplate.description,
      id: requirementTemplate.id,
      internalTitle: requirementTemplate.internalTitle,
      number: requirementTemplate.number,
      requirement: `${requirementTemplate.number.toString()}: ${requirementTemplate.title}`,
      requirementTemplateType: requirementTemplate.requirementTemplateType,
    };
  });
}

function renderSubComponent(requirementTemplate: TableData) {
  return {
    content: (
      <Flex pl={8}>
        <SubComponent requirementTemplate={requirementTemplate} />
      </Flex>
    ),
    width: 4,
  };
}

function SubComponent({ requirementTemplate }: { requirementTemplate: TableData }) {
  const { t } = useTranslation('AddRequirementsButton');

  return (
    <Flex direction="column" gap={3}>
      <Flex direction="column" gap={1}>
        <Text variant="labelM">{t('requirement')}</Text>
        <Text variant="bodyM">{requirementTemplate.requirement}</Text>
      </Flex>
      <Flex direction="column" gap={1}>
        <Text variant="labelM">{t('description')}</Text>
        <Text variant="bodyM">
          <RequirementDescription
            description={requirementTemplate.description}
            requirementType={requirementTemplate.requirementTemplateType}
            baseHeadingLevel={3}
          />
        </Text>
      </Flex>
    </Flex>
  );
}

function getSort(sort: string) {
  const [id, desc] = sort.split('.');
  return [
    {
      desc: desc === 'asc' ? false : true,
      id,
    },
  ];
}

function NoRequirementsFound() {
  const { t } = useTranslation('AddRequirementsButton');
  return (
    <Flex gap={4} direction="column" align="center" py={10}>
      <ApplicationRejectedIllustration />
      <Text variant="titleM">{t('noRequirementsFoundTitle')}</Text>
      <Text>{t('noRequirementsFoundMessage')}</Text>
    </Flex>
  );
}
